/** Native touch scrolling, mouse dragging and accessible mobile pagination. */
class BlakeMillTestimonials extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bm-testimonials__track');
    this.pagination = this.querySelector('.bm-testimonials__pagination');
    this.cards = [...this.track.children];
    this.mobile = matchMedia('(max-width: 749px)');
    this.positions = [];
    this.activeIndex = undefined;
    this.pendingIndex = null;
    this.suppressClick = false;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.buttons = this.cards.map((card, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bm-testimonials__dot';
      button.setAttribute('aria-label', `Go to testimonial ${index + 1} of ${this.cards.length}`);
      button.setAttribute('aria-controls', this.track.id);
      button.addEventListener('click', () => this.goTo(index), options);
      return button;
    });
    this.pagination.replaceChildren(...this.buttons);
    this.track.addEventListener('scroll', () => {
      if (this.frame || !this.enabled) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.measureFrame) this.update();
      });
    }, { ...options, passive: true });
    this.mobile.addEventListener('change', () => this.scheduleMeasure(), options);
    this.track.addEventListener('pointerdown', (event) => {
      if (!this.enabled || event.pointerType !== 'mouse' || event.button !== 0) return;
      this.suppressClick = false;
      if (event.target.closest('[data-testimonial-hotspot]')) return;
      this.drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft };
    }, options);
    this.track.addEventListener('pointermove', (event) => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const distance = event.clientX - this.drag.x;
      if (!this.suppressClick && Math.abs(distance) < 6) return;
      this.suppressClick = true;
      this.track.setPointerCapture(event.pointerId);
      this.track.classList.add('is-dragging');
      this.track.scrollLeft = this.drag.left - distance;
      event.preventDefault();
    }, options);
    const endDrag = () => {
      if (!this.drag) return;
      const index = this.nearest();
      if (this.track.hasPointerCapture(this.drag.id)) this.track.releasePointerCapture(this.drag.id);
      this.drag = null;
      this.track.classList.remove('is-dragging');
      if (this.suppressClick) this.goTo(index);
    };
    window.addEventListener('pointerup', endDrag, options);
    this.track.addEventListener('pointercancel', endDrag, options);
    this.track.addEventListener('lostpointercapture', endDrag, options);
    this.track.addEventListener('dragstart', (event) => {
      if (this.enabled) event.preventDefault();
    }, options);
    this.track.addEventListener('click', (event) => {
      if (!this.suppressClick || event.detail === 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }, { ...options, capture: true });
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.cards.indexOf(event.target.closest('.bm-testimonial'));
      if (index >= 0) this.goTo(index);
    }, options);
    this.resize = new ResizeObserver(() => this.scheduleMeasure());
    // ResizeObserver supplies the initial measurement after layout.
    this.resize.observe(this.track);
  }

  get enabled() {
    return this.mobile.matches && this.dataset.layout === 'swipe' && this.cards.length > 1;
  }

  scheduleMeasure() {
    if (this.measureFrame) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measure();
    });
  }

  measure() {
    this.positions = this.enabled ? this.targets() : [];
    this.update();
    if (this.pendingIndex !== null) {
      const index = this.pendingIndex;
      this.pendingIndex = null;
      this.goTo(index);
    }
  }

  targets() {
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const bounds = this.track.getBoundingClientRect();
    const max = this.track.scrollWidth - this.track.clientWidth;
    const left = this.track.scrollLeft;
    return this.cards.map((card) => {
      const rect = card.getBoundingClientRect();
      const position = left + (rtl ? rect.right - bounds.right : rect.left - bounds.left);
      return rtl ? Math.max(-max, Math.min(0, position)) : Math.max(0, Math.min(max, position));
    });
  }

  nearest() {
    const offset = this.track.scrollLeft;
    const distances = this.positions.map((left) => Math.abs(left - offset));
    return distances.indexOf(Math.min(...distances));
  }

  goTo(index) {
    if (!this.enabled) return;
    if (this.measureFrame || !this.positions.length) {
      this.pendingIndex = index;
      return;
    }
    this.track.scrollTo({
      left: this.positions[index],
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  update() {
    const enabled = this.enabled;
    const active = enabled && this.positions.length ? this.nearest() : 0;
    if (this.pagination.hidden !== !enabled) this.pagination.hidden = !enabled;
    if (active === this.activeIndex) return;
    this.activeIndex = active;
    this.buttons.forEach((button, index) => {
      if (index === active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }

  disconnectedCallback() {
    this.events.abort();
    this.resize.disconnect();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.measureFrame);
    this.frame = null;
    this.measureFrame = null;
    this.pendingIndex = null;
    this.drag = null;
    this.track.classList.remove('is-dragging');
  }
}

if (!customElements.get('bm-testimonial-carousel')) {
  customElements.define('bm-testimonial-carousel', BlakeMillTestimonials);
}
